import { useEffect, useId, useRef } from 'react';
import { HISTOGRAM_BUCKETS } from '@champdle/contracts';
import type { GameSummary, Stats } from '@champdle/contracts';
import { formatDuration } from '../board.js';
import { useGameHistory, useStats } from '../queries.js';
import type { View } from '../route.js';
import type { ChampionItem } from './GuessInput.js';

const percent = (v: number | null): string => (v === null ? '–' : `${Math.round(v * 100)}%`);
const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

function paceSentence(p: NonNullable<Stats['accuracy']['pace']>): string {
  const d = Math.abs(p.averageDelta);
  const gap =
    d < 0.05
      ? 'on par with the solver'
      : `${d.toFixed(1)} guesses ${p.averageDelta > 0 ? 'more' : 'fewer'} than the solver`;
  return `${gap} (you ${p.averageGuesses.toFixed(1)}, solver ${p.averagePar.toFixed(1)})`;
}

export function Histogram({ counts }: { counts: readonly number[] }) {
  const max = Math.max(1, ...counts);
  return (
    <ol className="histo" aria-label="Wins by number of guesses">
      {counts.map((count, i) => {
        const label = i === HISTOGRAM_BUCKETS - 1 ? `${i + 1}+` : String(i + 1);
        return (
          <li key={label} className="histo__row">
            <span className="histo__label" aria-hidden="true">
              {label}
            </span>
            <span className="histo__track" aria-hidden="true">
              <span className="histo__bar" style={{ width: `${(count / max) * 100}%` }} />
            </span>
            <span className="histo__count" aria-hidden="true">
              {count}
            </span>
            <span className="sr-only">
              {`${label} ${label === '1' ? 'guess' : 'guesses'}: ${plural(count, 'win')}`}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function GameRow({
  game,
  byId,
  go,
}: {
  game: GameSummary;
  byId: ReadonlyMap<string, ChampionItem>;
  go: (v: View) => void;
}) {
  const name = byId.get(game.answer.championId)?.name ?? game.answer.championId;
  const outcome =
    game.status === 'won' ? `Won in ${plural(game.guessCount, 'guess', 'guesses')}` : 'Gave up';
  return (
    <li className="games__item">
      <button type="button" className="games__button" onClick={() => go(`game/${game.id}`)}>
        <span className="games__name">{name}</span>
        <span className={`games__outcome games__outcome--${game.status}`}>{outcome}</span>
        <span className="games__meta">
          {formatDuration(game.elapsedMs)} · {new Date(game.endedAt).toLocaleDateString()}
        </span>
        <span className="sr-only">. Open replay</span>
      </button>
    </li>
  );
}

interface Props {
  byId: ReadonlyMap<string, ChampionItem>;
  go: (v: View) => void;
}

export function StatsPage({ byId, go }: Props) {
  const id = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const stats = useStats();
  const history = useGameHistory();
  useEffect(() => headingRef.current?.focus(), []);

  const s = stats.data;
  const games = history.data?.pages.flatMap((p) => p.games) ?? [];

  return (
    <section className="panel panel--wide" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} ref={headingRef} tabIndex={-1}>
        Your stats
      </h2>

      {stats.isError ? (
        <div className="notice" role="alert">
          <p>Could not load your stats.</p>
          <button type="button" onClick={() => void stats.refetch()}>
            Retry
          </button>
        </div>
      ) : !s ? (
        <p className="muted" role="status">
          Loading…
        </p>
      ) : s.played === 0 ? (
        <p className="muted">
          No finished games yet. Solve one or give up and it will show up here.
        </p>
      ) : (
        <>
          <dl className="statgrid">
            <Stat label="Played" value={String(s.played)} />
            <Stat label="Win rate" value={percent(s.winRate)} />
            <Stat label="Average guesses" value={s.averageGuesses?.toFixed(1) ?? '–'} />
            <Stat
              label="Best"
              value={s.bestGuesses === null ? '–' : plural(s.bestGuesses, 'guess', 'guesses')}
            />
            <Stat
              label="Fastest win"
              value={s.fastestMs === null ? '–' : formatDuration(s.fastestMs)}
            />
            <Stat label="Current streak" value={String(s.currentStreak)} />
            <Stat label="Best streak" value={String(s.bestStreak)} />
          </dl>

          <h3>Guesses per win</h3>
          {s.won === 0 ? <p className="muted">No wins yet.</p> : <Histogram counts={s.histogram} />}

          <h3>How well you play</h3>
          <dl className="accuracy">
            <div>
              <dt>Consistency</dt>
              <dd>{percent(s.accuracy.consistencyRate)}</dd>
              <dd className="hint">
                Guesses that could still have been the answer, given what you knew.
              </dd>
            </div>
            <div>
              <dt>Elimination efficiency</dt>
              <dd>{percent(s.accuracy.eliminationEfficiency)}</dd>
              <dd className="hint">
                How many candidates your guesses removed, against the best available guess.
              </dd>
            </div>
            <div>
              <dt>Pace</dt>
              <dd>{s.accuracy.pace ? paceSentence(s.accuracy.pace) : '–'}</dd>
              <dd className="hint">Your wins compared with the solver on the same answers.</dd>
            </div>
          </dl>
        </>
      )}

      <h3>Recent games</h3>
      {history.isError ? (
        <div className="notice" role="alert">
          <p>Could not load your games.</p>
          <button type="button" onClick={() => void history.refetch()}>
            Retry
          </button>
        </div>
      ) : games.length === 0 ? (
        <p className="muted">{history.isPending ? 'Loading…' : 'Nothing here yet.'}</p>
      ) : (
        <ul className="games" aria-label="Recent games, newest first">
          {games.map((g) => (
            <GameRow key={g.id} game={g} byId={byId} go={go} />
          ))}
        </ul>
      )}
      {history.hasNextPage ? (
        <div className="actions">
          <button
            type="button"
            disabled={history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
          >
            {history.isFetchingNextPage ? 'Loading…' : 'Show older games'}
          </button>
        </div>
      ) : null}

      <div className="actions">
        <button type="button" className="link" onClick={() => go('play')}>
          Back to the game
        </button>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
